import React, { useCallback, useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { PaymentStatus } from "@/components/PaymentStatus";
import { ReviewSheet } from "@/components/ReviewSheet";
import { ServiceRating } from "@/components/ServiceRating";
import { GiftShare } from "@/components/GiftShare";
import { FaqLink } from "@/pages/FaqPage";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { ApiError, cancelOrder, fetchMyReviews, fetchOrder, markArriving } from "@/lib/api";
import { formatMinutesLeft, formatOrderDate, formatUzs } from "@/lib/format";
import type { Order } from "@/types";
import { useBackButton } from "@/lib/hooks";
import { useCart } from "@/state/CartContext";
import { confirmAction, haptic } from "@/lib/telegram";
import { Ornament, OrderJourney } from "@/components/Premium";

const CANCELLABLE_STATUSES: Order["status"][] = ["new", "confirmed", "paid", "preparing", "ready_for_pickup"];

export function OrderDetailPage({ orderId }: { orderId: string }) {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  const { addItem } = useCart();
  const [order, setOrder] = useState<Order | null>(null);
  const [now, setNow] = useState(new Date());
  const [reviewed, setReviewed] = useState<Record<string, string>>({});
  const [reviewProduct, setReviewProduct] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [arriving, setArriving] = useState(false);

  const load = useCallback(() => {
    fetchOrder(orderId).then((o) => setOrder(o ?? null)).catch(() => {});
  }, [orderId]);

  useEffect(() => load(), [load]);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (order?.status !== "delivered") return;
    fetchMyReviews()
      .then((list) => setReviewed(Object.fromEntries(list.map((r) => [r.productId, r.status]))))
      .catch(() => {});
  }, [order?.status]);

  useBackButton(() => (reviewProduct ? setReviewProduct(null) : goBack()));

  if (!order) {
    return (
      <>
        <TopBar title="" onBack={goBack} />
        <main className="page page--no-nav">{t("common.loading")}</main>
      </>
    );
  }

  const minutesLeft = formatMinutesLeft(order.cancellableUntil, now);
  const canCancel = CANCELLABLE_STATUSES.includes(order.status) && minutesLeft > 0;

  const handleCancel = async () => {
    const confirmed = await confirmAction(t("orders.cancelConfirm", { id: order.number }));
    if (!confirmed) return;
    try {
      await cancelOrder(order.id);
      haptic("warning");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    }
  };

  const handleReorder = () => {
    order.items.forEach((item) => addItem(item.productId, item.variantId, item.quantity));
    haptic("success");
    navigate({ name: "cart" });
  };

  // One review button per product (an order can hold the same product twice).
  const uniqueProducts = Array.from(new Map<string, Order["items"][number]>(order.items.map((i: Order["items"][number]) => [i.productId, i] as const)).values());

  return (
    <>
      <TopBar title={order.number} onBack={goBack} showCart={false} />
      <main className="page page--no-nav">
        <div style={{ display: "flex", gap: 8, marginBottom: "var(--space-3)", flexWrap: "wrap" }}>
          <span className="badge badge--accent">{t(`orders.status.${order.status}`)}</span>
          <span className="badge badge--muted">{t(`orders.paymentStatus.${order.paymentStatus}`)}</span>
        </div>
        <p style={{ fontSize: 13, color: "var(--color-ink-soft)", marginBottom: "var(--space-4)" }}>{formatOrderDate(order.createdAt, locale)}</p>

        <Ornament title={t("journey.title")} />
        <OrderJourney order={order} />
        {order.status === "cancelled" && order.cancelReason && (
          <p style={{ fontSize: 13, color: "var(--color-ink-soft)", marginTop: -8 }}>{t("orders.reason", { reason: order.cancelReason })}</p>
        )}

        {order.status !== "cancelled" && order.paymentStatus !== "paid" && <PaymentStatus order={order} onSettled={load} />}

        <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)" }}>
          {order.items.map((item, i) => (
            <div key={i} className="total-row" style={{ padding: "8px 0", margin: 0, borderBottom: i < order.items.length - 1 ? "1px solid var(--color-line)" : "none" }}>
              <span>
                {item.productName}{item.variantLabel !== "Стандарт" ? ` · ${item.variantLabel}` : ""} × {item.quantity}
                {item.bundleTitle && <small className="order-line__bundle">{t("bundles.inSet", { title: item.bundleTitle })}</small>}
              </span>
              <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{formatUzs(item.priceUzs * item.quantity, locale)}</span>
            </div>
          ))}
          {order.deliveryFeeUzs > 0 && (
            <div className="total-row" style={{ marginTop: 8 }}>
              <span>{t("checkout.deliveryLine")}</span>
              <span>{formatUzs(order.deliveryFeeUzs, locale)}</span>
            </div>
          )}
          {order.discountUzs > 0 && (
            <div className="total-row total-row--discount">
              <span>{t("checkout.discountLine", { code: order.promoCode ?? "" })}</span>
              <span>−{formatUzs(order.discountUzs, locale)}</span>
            </div>
          )}
          {(order.giftCardUzs ?? 0) > 0 && (
            <div className="total-row total-row--discount">
              <span>{t("cert.lineShort")}</span>
              <span>−{formatUzs(order.giftCardUzs ?? 0, locale)}</span>
            </div>
          )}
          <div className="total-row total-row--grand">
            <span>{t("cart.total")}</span>
            <span>{formatUzs(order.totalUzs, locale)}</span>
          </div>
        </div>

        {order.deliveryMethod === "pickup" && (order.status === "preparing" || order.status === "ready_for_pickup") && (
          <div className="arrive-card">
            {order.arrivingAt ? (
              <>
                <strong>◆ {t("arriving.doneTitle")}</strong>
                <span>{t("arriving.doneBody")}</span>
              </>
            ) : (
              <>
                <strong>{t("arriving.title")}</strong>
                <span>{t("arriving.body")}</span>
                <button
                  className="btn btn--primary"
                  disabled={arriving}
                  onClick={async () => {
                    setArriving(true);
                    try {
                      const r = await markArriving(order.id);
                      haptic("success");
                      setOrder({ ...order, arrivingAt: r.arrivingAt });
                    } catch (err) {
                      setError(err instanceof ApiError ? err.message : t("common.error"));
                    } finally {
                      setArriving(false);
                    }
                  }}
                >
                  {t("arriving.button")}
                </button>
              </>
            )}
          </div>
        )}

        <GiftShare order={order} />

        <FaqLink category="returns" title={t("faq.orderHelp")} hint={t("faq.orderHelpHint")} />

        {order.deliverySlot && order.deliveryDate && (
          <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)", fontSize: 13.5 }}>
            <b>{t("delivery.when")}:</b>{" "}
            {new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { weekday: "short", day: "numeric", month: "long", timeZone: "Asia/Tashkent" }).format(new Date(order.deliveryDate))}, {order.deliverySlot}
          </div>
        )}

        {order.status === "delivered" && !order.feedbackRating && (
          <ServiceRating orderId={order.id} onDone={(rating) => setOrder({ ...order, feedbackRating: rating })} />
        )}
        {order.status === "delivered" && (
          <button className="faq-link" onClick={() => navigate({ name: "taste" })}>
            <span>
              <b>{t("taste.orderCta")}</b>
              <small>{t("taste.orderCtaHint")}</small>
            </span>
            <span aria-hidden="true">›</span>
          </button>
        )}
        {order.status === "delivered" && !!order.feedbackRating && (
          <p className="hint-line" style={{ textAlign: "center", marginBottom: 16 }}>{t("rating.thanks")}</p>
        )}

        {order.address && (
          <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)", fontSize: 13.5, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("checkout.addressTitle")}</div>
            <div>{order.address.fullName}</div>
            <div>{order.address.phone}</div>
            <div>{order.address.address}</div>
            {order.address.comment && <div style={{ color: "var(--color-ink-soft)" }}>{order.address.comment}</div>}
          </div>
        )}

        {order.status === "delivered" && (
          <div className="card" style={{ padding: "var(--space-4)", marginBottom: "var(--space-4)" }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>{t("product.reviewsTitle")}</div>
            {uniqueProducts.map((item) => (
              <div key={item.productId} className="total-row" style={{ alignItems: "center", margin: "6px 0" }}>
                <span style={{ fontSize: 13.5 }}>{item.productName}</span>
                {reviewed[item.productId] ? (
                  <span className="badge badge--success">{reviewed[item.productId] === "approved" ? t("reviews.approved") : t("reviews.pending")}</span>
                ) : (
                  <button className="btn btn--ghost" style={{ padding: "8px 12px", fontSize: 13 }} onClick={() => setReviewProduct({ id: item.productId, name: item.productName })}>
                    {t("reviews.leave")}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && <div className="form-error-banner">{error}</div>}

        {canCancel ? (
          <>
            <p style={{ fontSize: 12.5, color: "var(--color-muted)", marginBottom: 8 }}>{t("orders.cancelledIn", { minutes: minutesLeft })}</p>
            <button className="btn btn--danger-text" onClick={handleCancel}>
              {t("orders.cancel")}
            </button>
          </>
        ) : (
          order.status !== "cancelled" &&
          order.status !== "delivered" && <p style={{ fontSize: 12.5, color: "var(--color-muted)", marginBottom: 8 }}>{t("orders.cancelExpired")}</p>
        )}

        <button className="btn btn--ghost" style={{ width: "100%", marginTop: 12 }} onClick={handleReorder}>
          {t("orders.reorder")}
        </button>
      </main>

      <ReviewSheet
        product={reviewProduct}
        open={!!reviewProduct}
        onClose={() => setReviewProduct(null)}
        onSent={(productId) => setReviewed((prev) => ({ ...prev, [productId]: "pending" }))}
      />
    </>
  );
}
